// backup/frontend/src/section/become_member/AdminRegisterMember.jsx
import React, { useState } from 'react';
import {
  Box, Typography, Button, Stepper, Step, StepLabel,
  Card, CardContent, Dialog, DialogTitle, DialogContent,
  DialogActions, useMediaQuery, useTheme
} from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import becomeMemberServices from '../../services/becomeMemberServices';
import '../../components/MembershipForm/MembershipForm.css';

// ── helpers ──────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  first_name: '', middle_name: '', last_name: '', dob: '', email: '', phone: '',
  address: '', apt: '', city: '', state: '', zip: '', marital_status: '',
  beneficiary_first_name: '', beneficiary_middle_name: '', beneficiary_last_name: '',
  beneficiary_phone: '', beneficiary_address: '', beneficiary_apt: '',
  beneficiary_city: '', beneficiary_state: '', beneficiary_zip: '',
  beneficiary2_first_name: '', beneficiary2_middle_name: '', beneficiary2_last_name: '',
  beneficiary2_phone: '', beneficiary2_address: '', beneficiary2_apt: '',
  beneficiary2_city: '', beneficiary2_state: '', beneficiary2_zip: '',
  spouse_first_name: '', spouse_middle_name: '', spouse_last_name: '',
  spouse_dob: '', spouse_email: '', spouse_phone: '',
  children: [{ first_name: '', middle_name: '', last_name: '', relationship: '', gender: '', disabled: '', dob: '' }]
};

const steps = ['Applicant Information', 'Beneficiary Information', 'Spouse Information', 'Children Information', 'Review & Submit'];

// ── component ─────────────────────────────────────────────────────────────────
const AdminRegisterMember = ({ prefillData = null, editMode = false, editId = null, onSuccess = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // If prefillData is passed (edit mode), map it to flat formData shape
  const mapPrefillToForm = (data) => {
    if (!data) return EMPTY_FORM;
    return {
      first_name: data.applicant?.first_name || '',
      middle_name: data.applicant?.middle_name || '',
      last_name: data.applicant?.last_name || '',
      dob: data.applicant?.dob ? new Date(data.applicant.dob).toISOString().split('T')[0] : '',
      email: data.applicant?.email || '',
      phone: data.applicant?.phone || '',
      address: data.applicant?.address || '',
      apt: data.applicant?.apt || '',
      city: data.applicant?.city || '',
      state: data.applicant?.state || '',
      zip: data.applicant?.zip || '',
      marital_status: data.applicant?.marital_status || '',
      beneficiary_first_name: data.beneficiary?.first_name || '',
      beneficiary_middle_name: data.beneficiary?.middle_name || '',
      beneficiary_last_name: data.beneficiary?.last_name || '',
      beneficiary_phone: data.beneficiary?.phone || '',
      beneficiary_address: data.beneficiary?.address || '',
      beneficiary_apt: data.beneficiary?.apt || '',
      beneficiary_city: data.beneficiary?.city || '',
      beneficiary_state: data.beneficiary?.state || '',
      beneficiary_zip: data.beneficiary?.zip || '',
      beneficiary2_first_name: data.beneficiary2?.first_name || '',
      beneficiary2_middle_name: data.beneficiary2?.middle_name || '',
      beneficiary2_last_name: data.beneficiary2?.last_name || '',
      beneficiary2_phone: data.beneficiary2?.phone || '',
      beneficiary2_address: data.beneficiary2?.address || '',
      beneficiary2_apt: data.beneficiary2?.apt || '',
      beneficiary2_city: data.beneficiary2?.city || '',
      beneficiary2_state: data.beneficiary2?.state || '',
      beneficiary2_zip: data.beneficiary2?.zip || '',
      spouse_first_name: data.spouse?.first_name || '',
      spouse_middle_name: data.spouse?.middle_name || '',
      spouse_last_name: data.spouse?.last_name || '',
      spouse_dob: data.spouse?.dob ? new Date(data.spouse.dob).toISOString().split('T')[0] : '',
      spouse_email: data.spouse?.email || '',
      spouse_phone: data.spouse?.phone || '',
      children: data.children?.length
        ? data.children.map(c => ({
            first_name: c.first_name || '', middle_name: c.middle_name || '',
            last_name: c.last_name || '', relationship: c.relationship || '',
            gender: c.gender || '', disabled: c.disabled || '',
            dob: c.dob ? new Date(c.dob).toISOString().split('T')[0] : ''
          }))
        : [{ first_name: '', middle_name: '', last_name: '', relationship: '', gender: '', disabled: '', dob: '' }]
    };
  };

  // ── state ──────────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState(() => mapPrefillToForm(prefillData));
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [showBeneficiaryPrompt, setShowBeneficiaryPrompt] = useState(false);
  const [showSpousePrompt, setShowSpousePrompt] = useState(false);
  const [showChildrenPrompt, setShowChildrenPrompt] = useState(false);
  const [showBeneficiarySection, setShowBeneficiarySection] = useState(editMode);
  const [showSpouseSection, setShowSpouseSection] = useState(false);
  const [showChildrenSection, setShowChildrenSection] = useState(false);
  const [showSecondBeneficiary, setShowSecondBeneficiary] = useState(
    editMode && !!(prefillData?.beneficiary2?.first_name || prefillData?.beneficiary2?.last_name)
  );
  const [showEditSpouse, setShowEditSpouse] = useState(
    editMode && !!(prefillData?.spouse?.first_name || prefillData?.spouse?.last_name)
  );
  const [showBeneficiaryCountError, setShowBeneficiaryCountError] = useState(false);
  const [childCount, setChildCount] = useState(
    prefillData?.children?.length || 1
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // In edit mode, start at step 0 with all sections visible via stepper
  const [editSectionOpen, setEditSectionOpen] = useState(editMode ? 0 : null);

  // ── handlers ───────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleChildChange = (index, field, value) => {
    const updated = [...formData.children];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, children: updated }));
  };

  const addChild = () => {
    if (childCount < 7) {
      setFormData(prev => ({
        ...prev,
        children: [...prev.children, { first_name: '', middle_name: '', last_name: '', relationship: '', gender: '', disabled: '', dob: '' }]
      }));
      setChildCount(prev => prev + 1);
    }
  };

  const removeChild = (index) => {
    setFormData(prev => ({ ...prev, children: prev.children.filter((_, i) => i !== index) }));
    setChildCount(prev => Math.max(0, prev - 1));
    setErrors(prev => {
      const nextErrors = { ...prev };
      Object.keys(nextErrors).forEach(key => {
        if (key.startsWith(`child${index}_`)) delete nextErrors[key];
      });
      return nextErrors;
    });
  };

  const childEntries = editMode ? formData.children : formData.children.filter(c => c.first_name || c.last_name || c.relationship || c.gender || c.disabled || c.dob);
  const hasVisibleChildren = editMode ? formData.children.length > 0 : formData.children.some(c => c.first_name || c.last_name || c.relationship || c.gender || c.disabled || c.dob);

  const clearSpouseSection = () => {
    setFormData(prev => ({
      ...prev,
      spouse_first_name: '', spouse_middle_name: '', spouse_last_name: '',
      spouse_dob: '', spouse_email: '', spouse_phone: ''
    }));
    setShowEditSpouse(false);
    setErrors(prev => {
      const nextErrors = { ...prev };
      Object.keys(nextErrors).forEach(key => {
        if (key.startsWith('spouse_')) delete nextErrors[key];
      });
      return nextErrors;
    });
  };

  const clearBeneficiary2Section = () => {
    setFormData(prev => ({
      ...prev,
      beneficiary2_first_name: '', beneficiary2_middle_name: '', beneficiary2_last_name: '',
      beneficiary2_phone: '', beneficiary2_address: '', beneficiary2_apt: '',
      beneficiary2_city: '', beneficiary2_state: '', beneficiary2_zip: ''
    }));
    setShowSecondBeneficiary(false);
    setErrors(prev => {
      const nextErrors = { ...prev };
      Object.keys(nextErrors).forEach(key => {
        if (key.startsWith('beneficiary2_')) delete nextErrors[key];
      });
      return nextErrors;
    });
  };

  // ── validation ─────────────────────────────────────────────────────────────
  const validateApplicant = () => {
    const newErrors = {};
    ['first_name', 'last_name', 'dob', 'email', 'phone', 'address', 'city', 'state', 'zip', 'marital_status']
      .forEach(f => { if (!formData[f]?.trim()) newErrors[f] = 'This field is required'; });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Please enter a valid email address';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateBeneficiary = () => {
    const newErrors = {};
    ['beneficiary_first_name', 'beneficiary_last_name', 'beneficiary_address', 'beneficiary_city', 'beneficiary_state', 'beneficiary_zip']
      .forEach(f => { if (!formData[f]?.trim()) newErrors[f] = 'This field is required'; });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSpouse = () => {
    if (!showSpouseSection) return true;
    const newErrors = {};
    ['spouse_first_name', 'spouse_last_name', 'spouse_dob']
      .forEach(f => { if (!formData[f]?.trim()) newErrors[f] = 'This field is required'; });
    if (formData.spouse_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.spouse_email))
      newErrors.spouse_email = 'Please enter a valid email address';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateChildren = () => {
    if (!showChildrenSection) return true;
    const newErrors = {};
    formData.children.forEach((child, i) => {
      if (child.first_name || child.last_name || child.relationship || child.gender || child.disabled || child.dob) {
        if (!child.first_name) newErrors[`child${i}_first_name`] = `First name required for child ${i + 1}`;
        if (!child.last_name) newErrors[`child${i}_last_name`] = `Last name required for child ${i + 1}`;
        if (!child.relationship) newErrors[`child${i}_relationship`] = `Relationship required for child ${i + 1}`;
        if (!child.gender) newErrors[`child${i}_gender`] = `Gender required for child ${i + 1}`;
        if (!child.disabled) newErrors[`child${i}_disabled`] = `Disabled status required for child ${i + 1}`;
        if (!child.dob) newErrors[`child${i}_dob`] = `Date of birth required for child ${i + 1}`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── step navigation ────────────────────────────────────────────────────────
  const handleApplicantSubmit = () => {
    if (validateApplicant()) {
      setCompletedSteps(prev => new Set([...prev, 0]));
      setShowBeneficiaryPrompt(true);
    } else toast.error('Please fill in all required fields');
  };

  const handleBeneficiaryPromptResponse = (add) => {
    if (add) { setShowBeneficiaryPrompt(false); setShowBeneficiarySection(true); setCurrentStep(1); }
    else toast.error('A beneficiary is required to proceed.');
  };

  const handleBeneficiarySubmit = () => {
    if (!validateBeneficiary()) {
      toast.error('Please fill in all required beneficiary fields');
      return;
    }

    if (!showSecondBeneficiary && !showBeneficiaryCountError) {
      setShowBeneficiaryCountError(true);
      return;
    }

    setCompletedSteps(prev => new Set([...prev, 1]));
    setShowBeneficiarySection(false);
    setShowBeneficiaryCountError(false);
    setShowSpousePrompt(true);
  };

  const handleBeneficiaryAddSecondChoice = (addSecondBeneficiary) => {
    setShowBeneficiaryCountError(false);
    if (addSecondBeneficiary) {
      setShowSecondBeneficiary(true);
      return;
    }

    setCompletedSteps(prev => new Set([...prev, 1]));
    setShowBeneficiarySection(false);
    setShowSpousePrompt(true);
  };

  const handleSpousePromptResponse = (add) => {
    setShowSpousePrompt(false);
    if (add) { setShowSpouseSection(true); setCurrentStep(2); } else setShowChildrenPrompt(true);
  };

  const handleSpouseSubmit = () => {
    if (validateSpouse()) {
      setCompletedSteps(prev => new Set([...prev, 2]));
      setShowSpouseSection(false);
      setShowChildrenPrompt(true);
    } else toast.error('Please fill in all required spouse fields');
  };

  const handleChildrenPromptResponse = (add) => {
    setShowChildrenPrompt(false);
    if (add) { setShowChildrenSection(true); setCurrentStep(3); } else setCurrentStep(4);
  };

  const handleChildrenSubmit = () => {
    if (validateChildren()) {
      setCompletedSteps(prev => new Set([...prev, 3]));
      setShowChildrenSection(false);
      setCurrentStep(4);
    } else toast.error('Please complete all children information');
  };

  const handleEditSection = (s) => {
    switch (s) {
      case 0: setCurrentStep(0); setShowBeneficiarySection(false); setShowSpouseSection(false); setShowChildrenSection(false); break;
      case 1: setShowBeneficiarySection(true); setShowSpouseSection(false); setShowChildrenSection(false); setCurrentStep(1); break;
      case 2: setShowSpouseSection(true); setShowBeneficiarySection(false); setShowChildrenSection(false); setCurrentStep(2); break;
      case 3: setShowChildrenSection(true); setShowBeneficiarySection(false); setShowSpouseSection(false); setCurrentStep(3); break;
      default: break;
    }
  };

  const handleBackFromBeneficiary = () => { setShowBeneficiarySection(false); setCurrentStep(0); };
  const handleBackFromSpouse = () => { setShowSpouseSection(false); setShowBeneficiarySection(true); setCurrentStep(1); };
  const handleBackFromChildren = () => {
    setShowChildrenSection(false);
    if (formData.spouse_first_name || formData.spouse_last_name) { setShowSpouseSection(true); setCurrentStep(2); }
    else { setShowBeneficiarySection(true); setCurrentStep(1); }
  };
  const handleBackFromSummary = () => {
    if (formData.children.some(c => c.first_name || c.last_name)) { setShowChildrenSection(true); setCurrentStep(3); }
    else if (formData.spouse_first_name || formData.spouse_last_name) { setShowSpouseSection(true); setCurrentStep(2); }
    else { setShowBeneficiarySection(true); setCurrentStep(1); }
  };

  // ── final submit (register or update) ─────────────────────────────────────
  const handleFinalSubmit = async () => {
    if (!formData.beneficiary_first_name || !formData.beneficiary_last_name) {
      toast.error('You must add at least 1 beneficiary to complete the registration.');
      return;
    }

    setIsSubmitting(true);
    try {
      const validChildren = formData.children.filter(c =>
        c.first_name || c.middle_name || c.last_name || c.relationship || c.gender || c.disabled || c.dob
      );
      const hasSpouse = showEditSpouse || !!(formData.spouse_first_name || formData.spouse_last_name || formData.spouse_dob || formData.spouse_email || formData.spouse_phone);
      const hasBeneficiary2 = showSecondBeneficiary || !!(formData.beneficiary2_first_name || formData.beneficiary2_last_name || formData.beneficiary2_phone || formData.beneficiary2_address || formData.beneficiary2_city || formData.beneficiary2_state || formData.beneficiary2_zip);

      const payload = {
        applicant: {
          first_name: formData.first_name, middle_name: formData.middle_name,
          last_name: formData.last_name, dob: formData.dob, email: formData.email,
          phone: formData.phone, address: formData.address, apt: formData.apt,
          city: formData.city, state: formData.state, zip: formData.zip,
          marital_status: formData.marital_status
        },
        beneficiary: {
          first_name: formData.beneficiary_first_name, middle_name: formData.beneficiary_middle_name,
          last_name: formData.beneficiary_last_name, phone: formData.beneficiary_phone,
          address: formData.beneficiary_address, apt: formData.beneficiary_apt,
          city: formData.beneficiary_city, state: formData.beneficiary_state, zip: formData.beneficiary_zip
        },
        beneficiary2: hasBeneficiary2 ? {
          first_name: formData.beneficiary2_first_name, middle_name: formData.beneficiary2_middle_name,
          last_name: formData.beneficiary2_last_name, phone: formData.beneficiary2_phone,
          address: formData.beneficiary2_address, apt: formData.beneficiary2_apt,
          city: formData.beneficiary2_city, state: formData.beneficiary2_state, zip: formData.beneficiary2_zip
        } : null,
        spouse: hasSpouse ? {
          first_name: formData.spouse_first_name, middle_name: formData.spouse_middle_name,
          last_name: formData.spouse_last_name, dob: formData.spouse_dob,
          email: formData.spouse_email, phone: formData.spouse_phone
        } : null,
        children: validChildren,
        signature: { date: new Date().toISOString() }
      };

      // Call admin endpoint directly with fetch to get raw PDF blob
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const fetchUrl = editMode && editId
        ? `${baseURL}/admin/membership-applications/${editId}`
        : `${baseURL}/admin/membership-applications`;
      const method = editMode && editId ? 'PUT' : 'POST';

      const fetchResponse = await fetch(fetchUrl, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload),
      });

      if (!fetchResponse.ok) {
        const errText = await fetchResponse.text();
        throw new Error(errText || 'Server error');
      }

     
      toast.success(editMode ? 'Member information updated successfully!' : 'Member registered successfully!');

      if (onSuccess) {
        onSuccess(); // close modal / refresh list
      } else {
        navigate('/admin/becomeMember');
      }
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error(editMode ? 'Failed to update member information. Please try again.' : 'Failed to register member. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Box className="membership-form-container" sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 }, maxHeight: '85vh', overflowY: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        {editMode ? 'Edit Member Application' : 'Register New Member'}
      </Typography>

      {!editMode && (
        <Box sx={{ width: '100%', mb: 4 }}>
          <Stepper activeStep={currentStep} alternativeLabel={!isMobile} orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ '& .MuiStepLabel-root .Mui-completed': { color: '#4caf50' }, '& .MuiStepLabel-root .Mui-active': { color: '#2196f3' } }}>
            {steps.map((label, index) => (
              <Step key={label} completed={completedSteps.has(index)}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </Box>
      )}

      {editMode ? (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" className="section-title" sx={{ mb: 3 }}>APPLICANT INFORMATION</Typography>
              <table className="form-table"><tbody>
                <tr>
                  <td width="33%">First Name: <input type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} />{errors.first_name && <div className="error">{errors.first_name}</div>}</td>
                  <td width="33%">Middle Name: <input type="text" name="middle_name" value={formData.middle_name} onChange={handleInputChange} /></td>
                  <td width="33%">Last Name: <input type="text" name="last_name" value={formData.last_name} onChange={handleInputChange} />{errors.last_name && <div className="error">{errors.last_name}</div>}</td>
                </tr>
                <tr>
                  <td>Date of Birth: <input type="date" name="dob" value={formData.dob} onChange={handleInputChange} />{errors.dob && <div className="error">{errors.dob}</div>}</td>
                  <td>E-Mail: <input type="email" name="email" value={formData.email} onChange={handleInputChange} />{errors.email && <div className="error">{errors.email}</div>}</td>
                  <td>Phone: <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} />{errors.phone && <div className="error">{errors.phone}</div>}</td>
                </tr>
                <tr>
                  <td colSpan="2">Current Address: <input type="text" name="address" value={formData.address} onChange={handleInputChange} />{errors.address && <div className="error">{errors.address}</div>}</td>
                  <td>Apt. #: <input type="text" name="apt" value={formData.apt} onChange={handleInputChange} /></td>
                </tr>
                <tr>
                  <td>City: <input type="text" name="city" value={formData.city} onChange={handleInputChange} />{errors.city && <div className="error">{errors.city}</div>}</td>
                  <td>State: <input type="text" name="state" value={formData.state} onChange={handleInputChange} />{errors.state && <div className="error">{errors.state}</div>}</td>
                  <td>Zip: <input type="text" name="zip" value={formData.zip} onChange={handleInputChange} />{errors.zip && <div className="error">{errors.zip}</div>}</td>
                </tr>
                <tr>
                  <td>Marital Status:
                    <select name="marital_status" value={formData.marital_status} onChange={handleInputChange}>
                      <option value="">Select...</option>
                      <option value="Single">Single</option><option value="Married">Married</option>
                      <option value="Divorced">Divorced</option><option value="Widowed">Widowed</option>
                    </select>
                    {errors.marital_status && <div className="error">{errors.marital_status}</div>}
                  </td>
                </tr>
              </tbody></table>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" className="section-title" sx={{ mb: 3 }}>BENEFICIARY INFORMATION</Typography>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Beneficiary 1</Typography>
              <table className="form-table"><tbody>
                <tr>
                  <td width="33%">First Name: <input type="text" name="beneficiary_first_name" value={formData.beneficiary_first_name} onChange={handleInputChange} />{errors.beneficiary_first_name && <div className="error">{errors.beneficiary_first_name}</div>}</td>
                  <td width="33%">Middle Name: <input type="text" name="beneficiary_middle_name" value={formData.beneficiary_middle_name} onChange={handleInputChange} /></td>
                  <td width="33%">Last Name: <input type="text" name="beneficiary_last_name" value={formData.beneficiary_last_name} onChange={handleInputChange} />{errors.beneficiary_last_name && <div className="error">{errors.beneficiary_last_name}</div>}</td>
                </tr>
                <tr><td colSpan="3">Phone: <input type="tel" name="beneficiary_phone" value={formData.beneficiary_phone} onChange={handleInputChange} /></td></tr>
                <tr>
                  <td colSpan="2">Address: <input type="text" name="beneficiary_address" value={formData.beneficiary_address} onChange={handleInputChange} />{errors.beneficiary_address && <div className="error">{errors.beneficiary_address}</div>}</td>
                  <td>Apt. #: <input type="text" name="beneficiary_apt" value={formData.beneficiary_apt} onChange={handleInputChange} /></td>
                </tr>
                <tr>
                  <td>City: <input type="text" name="beneficiary_city" value={formData.beneficiary_city} onChange={handleInputChange} />{errors.beneficiary_city && <div className="error">{errors.beneficiary_city}</div>}</td>
                  <td>State: <input type="text" name="beneficiary_state" value={formData.beneficiary_state} onChange={handleInputChange} />{errors.beneficiary_state && <div className="error">{errors.beneficiary_state}</div>}</td>
                  <td>Zip: <input type="text" name="beneficiary_zip" value={formData.beneficiary_zip} onChange={handleInputChange} />{errors.beneficiary_zip && <div className="error">{errors.beneficiary_zip}</div>}</td>
                </tr>
              </tbody></table>

              {(showSecondBeneficiary || formData.beneficiary2_first_name || formData.beneficiary2_last_name) && (
                <React.Fragment>
                  <Box sx={{ mt: 4, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Beneficiary 2</Typography>
                    <Button color="error" variant="outlined" size="small" onClick={clearBeneficiary2Section}>
                      Remove
                    </Button>
                  </Box>
                  <table className="form-table"><tbody>
                    <tr>
                      <td width="33%">First Name: <input type="text" name="beneficiary2_first_name" value={formData.beneficiary2_first_name} onChange={handleInputChange} />{errors.beneficiary2_first_name && <div className="error">{errors.beneficiary2_first_name}</div>}</td>
                      <td width="33%">Middle Name: <input type="text" name="beneficiary2_middle_name" value={formData.beneficiary2_middle_name} onChange={handleInputChange} /></td>
                      <td width="33%">Last Name: <input type="text" name="beneficiary2_last_name" value={formData.beneficiary2_last_name} onChange={handleInputChange} />{errors.beneficiary2_last_name && <div className="error">{errors.beneficiary2_last_name}</div>}</td>
                    </tr>
                    <tr><td colSpan="3">Phone: <input type="tel" name="beneficiary2_phone" value={formData.beneficiary2_phone} onChange={handleInputChange} /></td></tr>
                    <tr>
                      <td colSpan="2">Address: <input type="text" name="beneficiary2_address" value={formData.beneficiary2_address} onChange={handleInputChange} />{errors.beneficiary2_address && <div className="error">{errors.beneficiary2_address}</div>}</td>
                      <td>Apt. #: <input type="text" name="beneficiary2_apt" value={formData.beneficiary2_apt} onChange={handleInputChange} /></td>
                    </tr>
                    <tr>
                      <td>City: <input type="text" name="beneficiary2_city" value={formData.beneficiary2_city} onChange={handleInputChange} />{errors.beneficiary2_city && <div className="error">{errors.beneficiary2_city}</div>}</td>
                      <td>State: <input type="text" name="beneficiary2_state" value={formData.beneficiary2_state} onChange={handleInputChange} />{errors.beneficiary2_state && <div className="error">{errors.beneficiary2_state}</div>}</td>
                      <td>Zip: <input type="text" name="beneficiary2_zip" value={formData.beneficiary2_zip} onChange={handleInputChange} />{errors.beneficiary2_zip && <div className="error">{errors.beneficiary2_zip}</div>}</td>
                    </tr>
                  </tbody></table>
                </React.Fragment>
              )}

              {editMode && !showSecondBeneficiary && !formData.beneficiary2_first_name && !formData.beneficiary2_last_name && (
                <Box sx={{ mt: 3 }}>
                  <Button variant="outlined" onClick={() => setShowSecondBeneficiary(true)}
                    sx={{ borderColor: '#28a745', color: '#28a745', '&:hover': { borderColor: '#218838' } }}>
                    Add Second Beneficiary
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {editMode && !showEditSpouse && !formData.spouse_first_name && !formData.spouse_last_name && (
            <Box sx={{ mb: 3 }}>
              <Button variant="outlined" onClick={() => setShowEditSpouse(true)}
                sx={{ borderColor: '#28a745', color: '#28a745', '&:hover': { borderColor: '#218838' } }}>
                Add Spouse
              </Button>
            </Box>
          )}

          {(showEditSpouse || formData.spouse_first_name || formData.spouse_last_name || formData.spouse_dob || formData.spouse_email || formData.spouse_phone) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" className="section-title">SPOUSE INFORMATION</Typography>
                  <Button color="error" variant="outlined" size="small" onClick={clearSpouseSection}>
                    Remove Spouse
                  </Button>
                </Box>
                <table className="form-table"><tbody>
                  <tr>
                    <td width="33%">First Name: <input type="text" name="spouse_first_name" value={formData.spouse_first_name} onChange={handleInputChange} />{errors.spouse_first_name && <div className="error">{errors.spouse_first_name}</div>}</td>
                    <td width="33%">Middle Name: <input type="text" name="spouse_middle_name" value={formData.spouse_middle_name} onChange={handleInputChange} /></td>
                    <td width="33%">Last Name: <input type="text" name="spouse_last_name" value={formData.spouse_last_name} onChange={handleInputChange} />{errors.spouse_last_name && <div className="error">{errors.spouse_last_name}</div>}</td>
                  </tr>
                  <tr>
                    <td>Date of Birth: <input type="date" name="spouse_dob" value={formData.spouse_dob} onChange={handleInputChange} />{errors.spouse_dob && <div className="error">{errors.spouse_dob}</div>}</td>
                    <td>E-Mail: <input type="email" name="spouse_email" value={formData.spouse_email} onChange={handleInputChange} />{errors.spouse_email && <div className="error">{errors.spouse_email}</div>}</td>
                    <td>Phone: <input type="tel" name="spouse_phone" value={formData.spouse_phone} onChange={handleInputChange} /></td>
                  </tr>
                </tbody></table>
              </CardContent>
            </Card>
          )}

          {editMode && !formData.children.some(c => c.first_name || c.last_name || c.relationship || c.gender || c.disabled || c.dob) && formData.children.length <= 1 && (
            <Box sx={{ mb: 3 }}>
              <Button variant="outlined" onClick={addChild}
                sx={{ borderColor: '#28a745', color: '#28a745', '&:hover': { borderColor: '#218838' } }}>
                Add Child
              </Button>
            </Box>
          )}

          {hasVisibleChildren && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" className="section-title" sx={{ mb: 3 }}>CHILDREN INFORMATION</Typography>
                {childEntries.map((child, index) => (
                  <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Child {index + 1}</Typography>
                      <Button color="error" variant="outlined" size="small" onClick={() => removeChild(index)}>
                        Remove
                      </Button>
                    </Box>
                    <table className="form-table"><tbody>
                      <tr>
                        <td width="33%">First Name: <input type="text" value={child.first_name} onChange={(e) => handleChildChange(index, 'first_name', e.target.value)} />{errors[`child${index}_first_name`] && <div className="error">{errors[`child${index}_first_name`]}</div>}</td>
                        <td width="33%">Middle Name: <input type="text" value={child.middle_name} onChange={(e) => handleChildChange(index, 'middle_name', e.target.value)} /></td>
                        <td width="34%">Last Name: <input type="text" value={child.last_name} onChange={(e) => handleChildChange(index, 'last_name', e.target.value)} />{errors[`child${index}_last_name`] && <div className="error">{errors[`child${index}_last_name`]}</div>}</td>
                      </tr>
                      <tr>
                        <td colSpan="3">Relationship:
                          <select value={child.relationship} onChange={(e) => handleChildChange(index, 'relationship', e.target.value)}>
                            <option value="">Select...</option><option value="Son">Son</option>
                            <option value="Daughter">Daughter</option><option value="Stepson">Stepson</option>
                            <option value="Stepdaughter">Stepdaughter</option>
                          </select>
                          {errors[`child${index}_relationship`] && <div className="error">{errors[`child${index}_relationship`]}</div>}
                        </td>
                      </tr>
                      <tr>
                        <td>Gender:
                          <select value={child.gender} onChange={(e) => handleChildChange(index, 'gender', e.target.value)}>
                            <option value="">Select...</option><option value="Male">Male</option><option value="Female">Female</option>
                          </select>
                          {errors[`child${index}_gender`] && <div className="error">{errors[`child${index}_gender`]}</div>}
                        </td>
                        <td>Date of Birth: <input type="date" value={child.dob} onChange={(e) => handleChildChange(index, 'dob', e.target.value)} />{errors[`child${index}_dob`] && <div className="error">{errors[`child${index}_dob`]}</div>}</td>
                        <td>Disabled:
                          <select value={child.disabled} onChange={(e) => handleChildChange(index, 'disabled', e.target.value)}>
                            <option value="">Select...</option><option value="Yes">Yes</option><option value="No">No</option>
                          </select>
                          {errors[`child${index}_disabled`] && <div className="error">{errors[`child${index}_disabled`]}</div>}
                        </td>
                      </tr>
                    </tbody></table>
                  </Box>
                ))}
                {editMode && (
                  <Box sx={{ mt: 3 }}>
                    <Button variant="outlined" onClick={addChild}
                      sx={{ borderColor: '#28a745', color: '#28a745', '&:hover': { borderColor: '#218838' } }}>
                      Add a Child
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleFinalSubmit} size="large" disabled={isSubmitting}
              sx={{ minWidth: 220, fontSize: '1rem', backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold', padding: '12px 24px' }}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </>
      ) : (
        <>
          {currentStep === 0 && !showBeneficiarySection && !showSpouseSection && !showChildrenSection && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" className="section-title" sx={{ mb: 3 }}>APPLICANT INFORMATION</Typography>
                <table className="form-table"><tbody>
                  <tr>
                    <td width="33%">First Name: <input type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} />{errors.first_name && <div className="error">{errors.first_name}</div>}</td>
                    <td width="33%">Middle Name: <input type="text" name="middle_name" value={formData.middle_name} onChange={handleInputChange} /></td>
                    <td width="33%">Last Name: <input type="text" name="last_name" value={formData.last_name} onChange={handleInputChange} />{errors.last_name && <div className="error">{errors.last_name}</div>}</td>
                  </tr>
                  <tr>
                    <td>Date of Birth: <input type="date" name="dob" value={formData.dob} onChange={handleInputChange} />{errors.dob && <div className="error">{errors.dob}</div>}</td>
                    <td>E-Mail: <input type="email" name="email" value={formData.email} onChange={handleInputChange} />{errors.email && <div className="error">{errors.email}</div>}</td>
                    <td>Phone: <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} />{errors.phone && <div className="error">{errors.phone}</div>}</td>
                  </tr>
                  <tr>
                    <td colSpan="2">Current Address: <input type="text" name="address" value={formData.address} onChange={handleInputChange} />{errors.address && <div className="error">{errors.address}</div>}</td>
                    <td>Apt. #: <input type="text" name="apt" value={formData.apt} onChange={handleInputChange} /></td>
                  </tr>
                  <tr>
                    <td>City: <input type="text" name="city" value={formData.city} onChange={handleInputChange} />{errors.city && <div className="error">{errors.city}</div>}</td>
                    <td>State: <input type="text" name="state" value={formData.state} onChange={handleInputChange} />{errors.state && <div className="error">{errors.state}</div>}</td>
                    <td>Zip: <input type="text" name="zip" value={formData.zip} onChange={handleInputChange} />{errors.zip && <div className="error">{errors.zip}</div>}</td>
                  </tr>
                  <tr>
                    <td>Marital Status:
                      <select name="marital_status" value={formData.marital_status} onChange={handleInputChange}>
                        <option value="">Select...</option>
                        <option value="Single">Single</option><option value="Married">Married</option>
                        <option value="Divorced">Divorced</option><option value="Widowed">Widowed</option>
                      </select>
                      {errors.marital_status && <div className="error">{errors.marital_status}</div>}
                    </td>
                  </tr>
                </tbody></table>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" onClick={handleApplicantSubmit}
                    sx={{ minWidth: 120, backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold' }}>
                    Continue
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          <Dialog open={showBeneficiaryPrompt} onClose={() => {}} maxWidth="sm" fullWidth>
            <DialogTitle>Add Beneficiary</DialogTitle>
            <DialogContent><Typography>Add a beneficiary for this member? (Required)</Typography></DialogContent>
            <DialogActions>
              <Button onClick={() => handleBeneficiaryPromptResponse(false)} color="secondary">No</Button>
              <Button onClick={() => handleBeneficiaryPromptResponse(true)} variant="contained"
                sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold' }}>
                Yes, Add Beneficiary
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={showBeneficiaryCountError} onClose={() => setShowBeneficiaryCountError(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Add Second Beneficiary?</DialogTitle>
            <DialogContent><Typography>Would you like to add a second beneficiary?</Typography></DialogContent>
            <DialogActions>
              <Button onClick={() => handleBeneficiaryAddSecondChoice(false)} color="secondary">No</Button>
              <Button onClick={() => handleBeneficiaryAddSecondChoice(true)} variant="contained"
                sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold' }}>Yes</Button>
            </DialogActions>
          </Dialog>

          {showBeneficiarySection && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" className="section-title" sx={{ mb: 3 }}>BENEFICIARY INFORMATION</Typography>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Beneficiary 1</Typography>
                <table className="form-table"><tbody>
                  <tr>
                    <td width="33%">First Name: <input type="text" name="beneficiary_first_name" value={formData.beneficiary_first_name} onChange={handleInputChange} />{errors.beneficiary_first_name && <div className="error">{errors.beneficiary_first_name}</div>}</td>
                    <td width="33%">Middle Name: <input type="text" name="beneficiary_middle_name" value={formData.beneficiary_middle_name} onChange={handleInputChange} /></td>
                    <td width="33%">Last Name: <input type="text" name="beneficiary_last_name" value={formData.beneficiary_last_name} onChange={handleInputChange} />{errors.beneficiary_last_name && <div className="error">{errors.beneficiary_last_name}</div>}</td>
                  </tr>
                  <tr><td colSpan="3">Phone: <input type="tel" name="beneficiary_phone" value={formData.beneficiary_phone} onChange={handleInputChange} /></td></tr>
                  <tr>
                    <td colSpan="2">Address: <input type="text" name="beneficiary_address" value={formData.beneficiary_address} onChange={handleInputChange} />{errors.beneficiary_address && <div className="error">{errors.beneficiary_address}</div>}</td>
                    <td>Apt. #: <input type="text" name="beneficiary_apt" value={formData.beneficiary_apt} onChange={handleInputChange} /></td>
                  </tr>
                  <tr>
                    <td>City: <input type="text" name="beneficiary_city" value={formData.beneficiary_city} onChange={handleInputChange} />{errors.beneficiary_city && <div className="error">{errors.beneficiary_city}</div>}</td>
                    <td>State: <input type="text" name="beneficiary_state" value={formData.beneficiary_state} onChange={handleInputChange} />{errors.beneficiary_state && <div className="error">{errors.beneficiary_state}</div>}</td>
                    <td>Zip: <input type="text" name="beneficiary_zip" value={formData.beneficiary_zip} onChange={handleInputChange} />{errors.beneficiary_zip && <div className="error">{errors.beneficiary_zip}</div>}</td>
                  </tr>
                </tbody></table>

                {showSecondBeneficiary && (
                  <React.Fragment>
                    <Box sx={{ mt: 4, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Beneficiary 2</Typography><Button color="error" variant="outlined" size="small" onClick={clearBeneficiary2Section}>Remove</Button></Box>
                    <table className="form-table"><tbody>
                      <tr>
                        <td width="33%">First Name: <input type="text" name="beneficiary2_first_name" value={formData.beneficiary2_first_name} onChange={handleInputChange} />{errors.beneficiary2_first_name && <div className="error">{errors.beneficiary2_first_name}</div>}</td>
                        <td width="33%">Middle Name: <input type="text" name="beneficiary2_middle_name" value={formData.beneficiary2_middle_name} onChange={handleInputChange} /></td>
                        <td width="33%">Last Name: <input type="text" name="beneficiary2_last_name" value={formData.beneficiary2_last_name} onChange={handleInputChange} />{errors.beneficiary2_last_name && <div className="error">{errors.beneficiary2_last_name}</div>}</td>
                      </tr>
                      <tr><td colSpan="3">Phone: <input type="tel" name="beneficiary2_phone" value={formData.beneficiary2_phone} onChange={handleInputChange} /></td></tr>
                      <tr>
                        <td colSpan="2">Address: <input type="text" name="beneficiary2_address" value={formData.beneficiary2_address} onChange={handleInputChange} />{errors.beneficiary2_address && <div className="error">{errors.beneficiary2_address}</div>}</td>
                        <td>Apt. #: <input type="text" name="beneficiary2_apt" value={formData.beneficiary2_apt} onChange={handleInputChange} /></td>
                      </tr>
                      <tr>
                        <td>City: <input type="text" name="beneficiary2_city" value={formData.beneficiary2_city} onChange={handleInputChange} />{errors.beneficiary2_city && <div className="error">{errors.beneficiary2_city}</div>}</td>
                        <td>State: <input type="text" name="beneficiary2_state" value={formData.beneficiary2_state} onChange={handleInputChange} />{errors.beneficiary2_state && <div className="error">{errors.beneficiary2_state}</div>}</td>
                        <td>Zip: <input type="text" name="beneficiary2_zip" value={formData.beneficiary2_zip} onChange={handleInputChange} />{errors.beneficiary2_zip && <div className="error">{errors.beneficiary2_zip}</div>}</td>
                      </tr>
                    </tbody></table>
                  </React.Fragment>
                )}

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="contained" onClick={handleBackFromBeneficiary}
                      sx={{ minWidth: 120, backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' } }}>Back</Button>
                    {!showSecondBeneficiary && (
                      <Button variant="outlined" onClick={() => setShowSecondBeneficiary(true)}
                        sx={{ minWidth: 160, borderColor: '#28a745', color: '#28a745', '&:hover': { borderColor: '#218838' }, fontWeight: 'bold' }}>
                        Add Beneficiary 2
                      </Button>
                    )}
                  </Box>
                  <Button variant="contained" onClick={handleBeneficiarySubmit}
                    sx={{ minWidth: 120, backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold' }}>
                    Continue
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          <Dialog open={showSpousePrompt} onClose={() => {}} maxWidth="sm" fullWidth>
            <DialogTitle>Add Spouse</DialogTitle>
            <DialogContent><Typography>Would you like to add spouse information?</Typography></DialogContent>
            <DialogActions>
              <Button onClick={() => handleSpousePromptResponse(false)} color="secondary">No, Skip</Button>
              <Button onClick={() => handleSpousePromptResponse(true)} variant="contained"
                sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold' }}>
                Yes, Add Spouse
              </Button>
            </DialogActions>
          </Dialog>

          {showSpouseSection && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" className="section-title" sx={{ mb: 3 }}>SPOUSE INFORMATION</Typography>
                <table className="form-table"><tbody>
                  <tr>
                    <td width="33%">First Name: <input type="text" name="spouse_first_name" value={formData.spouse_first_name} onChange={handleInputChange} />{errors.spouse_first_name && <div className="error">{errors.spouse_first_name}</div>}</td>
                    <td width="33%">Middle Name: <input type="text" name="spouse_middle_name" value={formData.spouse_middle_name} onChange={handleInputChange} /></td>
                    <td width="33%">Last Name: <input type="text" name="spouse_last_name" value={formData.spouse_last_name} onChange={handleInputChange} />{errors.spouse_last_name && <div className="error">{errors.spouse_last_name}</div>}</td>
                  </tr>
                  <tr>
                    <td>Date of Birth: <input type="date" name="spouse_dob" value={formData.spouse_dob} onChange={handleInputChange} />{errors.spouse_dob && <div className="error">{errors.spouse_dob}</div>}</td>
                    <td>E-Mail: <input type="email" name="spouse_email" value={formData.spouse_email} onChange={handleInputChange} />{errors.spouse_email && <div className="error">{errors.spouse_email}</div>}</td>
                    <td>Phone: <input type="tel" name="spouse_phone" value={formData.spouse_phone} onChange={handleInputChange} /></td>
                  </tr>
                </tbody></table>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Button variant="contained" onClick={handleBackFromSpouse}
                    sx={{ minWidth: 120, backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' } }}>Back</Button>
                  <Button variant="contained" onClick={handleSpouseSubmit}
                    sx={{ minWidth: 120, backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold' }}>Continue</Button>
                </Box>
              </CardContent>
            </Card>
          )}

          <Dialog open={showChildrenPrompt} onClose={() => {}} maxWidth="sm" fullWidth>
            <DialogTitle>Add Children</DialogTitle>
            <DialogContent><Typography>Would you like to add children information?</Typography></DialogContent>
            <DialogActions>
              <Button onClick={() => handleChildrenPromptResponse(false)} color="secondary">No, Skip</Button>
              <Button onClick={() => handleChildrenPromptResponse(true)} variant="contained"
                sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold' }}>
                Yes, Add Children
              </Button>
            </DialogActions>
          </Dialog>

          {showChildrenSection && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" className="section-title" sx={{ mb: 3 }}>CHILDREN INFORMATION</Typography>
                {formData.children.map((child, index) => (
                  <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Child {index + 1}
                      {childCount > 1 && <Button onClick={() => removeChild(index)} color="error" size="small" sx={{ ml: 2 }}>Remove</Button>}
                    </Typography>
                    <table className="form-table"><tbody>
                      <tr>
                        <td width="33%">First Name: <input type="text" value={child.first_name} onChange={(e) => handleChildChange(index, 'first_name', e.target.value)} />{errors[`child${index}_first_name`] && <div className="error">{errors[`child${index}_first_name`]}</div>}</td>
                        <td width="33%">Middle Name: <input type="text" value={child.middle_name} onChange={(e) => handleChildChange(index, 'middle_name', e.target.value)} /></td>
                        <td width="34%">Last Name: <input type="text" value={child.last_name} onChange={(e) => handleChildChange(index, 'last_name', e.target.value)} />{errors[`child${index}_last_name`] && <div className="error">{errors[`child${index}_last_name`]}</div>}</td>
                      </tr>
                      <tr>
                        <td colSpan="3">Relationship:
                          <select value={child.relationship} onChange={(e) => handleChildChange(index, 'relationship', e.target.value)}>
                            <option value="">Select...</option><option value="Son">Son</option>
                            <option value="Daughter">Daughter</option><option value="Stepson">Stepson</option>
                            <option value="Stepdaughter">Stepdaughter</option>
                          </select>
                          {errors[`child${index}_relationship`] && <div className="error">{errors[`child${index}_relationship`]}</div>}
                        </td>
                      </tr>
                      <tr>
                        <td>Gender:
                          <select value={child.gender} onChange={(e) => handleChildChange(index, 'gender', e.target.value)}>
                            <option value="">Select...</option><option value="Male">Male</option><option value="Female">Female</option>
                          </select>
                          {errors[`child${index}_gender`] && <div className="error">{errors[`child${index}_gender`]}</div>}
                        </td>
                        <td>Date of Birth: <input type="date" value={child.dob} onChange={(e) => handleChildChange(index, 'dob', e.target.value)} />{errors[`child${index}_dob`] && <div className="error">{errors[`child${index}_dob`]}</div>}</td>
                        <td>Disabled:
                          <select value={child.disabled} onChange={(e) => handleChildChange(index, 'disabled', e.target.value)}>
                            <option value="">Select...</option><option value="Yes">Yes</option><option value="No">No</option>
                          </select>
                          {errors[`child${index}_disabled`] && <div className="error">{errors[`child${index}_disabled`]}</div>}
                        </td>
                      </tr>
                    </tbody></table>
                  </Box>
                ))}
                {childCount < 7 && (
                  <Button variant="outlined" onClick={addChild}
                    sx={{ mb: 3, borderColor: '#28a745', color: '#28a745', '&:hover': { borderColor: '#218838' } }}>
                    Add a Child
                  </Button>
                )}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Button variant="contained" onClick={handleBackFromChildren}
                    sx={{ minWidth: 120, backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' } }}>Back</Button>
                  <Button variant="contained" onClick={handleChildrenSubmit}
                    sx={{ minWidth: 120, backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold' }}>Continue</Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {currentStep === 4 && !showBeneficiarySection && !showSpouseSection && !showChildrenSection && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" className="section-title" sx={{ mb: 3 }}>REVIEW & REGISTER MEMBER</Typography>
                <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Applicant Information</Typography>
                    <Button size="small" variant="outlined" onClick={() => handleEditSection(0)}
                      sx={{ borderColor: '#2196f3', color: '#2196f3' }}>Edit</Button>
                  </Box>
                  <Typography>{formData.first_name} {formData.middle_name} {formData.last_name}</Typography>
                  <Typography>DOB: {formData.dob}</Typography>
                  <Typography>{formData.email} | {formData.phone}</Typography>
                  <Typography>{formData.address}{formData.apt ? `, Apt ${formData.apt}` : ''}, {formData.city}, {formData.state} {formData.zip}</Typography>
                  <Typography>Marital Status: {formData.marital_status}</Typography>
                </Box>

                <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Beneficiary Information</Typography>
                    <Button size="small" variant="outlined" onClick={() => handleEditSection(1)}
                      sx={{ borderColor: '#2196f3', color: '#2196f3' }}>Edit</Button>
                  </Box>
                  <Typography sx={{ fontWeight: 'bold', mb: 0.5 }}>Beneficiary 1</Typography>
                  <Typography>{formData.beneficiary_first_name} {formData.beneficiary_middle_name} {formData.beneficiary_last_name}</Typography>
                  <Typography>Phone: {formData.beneficiary_phone}</Typography>
                  <Typography sx={{ mb: 2 }}>{formData.beneficiary_address}, {formData.beneficiary_city}, {formData.beneficiary_state} {formData.beneficiary_zip}</Typography>
                  <Typography sx={{ fontWeight: 'bold', mb: 0.5 }}>Beneficiary 2</Typography>
                  <Typography>{formData.beneficiary2_first_name} {formData.beneficiary2_middle_name} {formData.beneficiary2_last_name}</Typography>
                  <Typography>Phone: {formData.beneficiary2_phone}</Typography>
                  <Typography>{formData.beneficiary2_address}, {formData.beneficiary2_city}, {formData.beneficiary2_state} {formData.beneficiary2_zip}</Typography>
                </Box>

                {(formData.spouse_first_name || formData.spouse_last_name) && (
                  <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Spouse Information</Typography>
                      <Button size="small" variant="outlined" onClick={() => handleEditSection(2)}
                        sx={{ borderColor: '#2196f3', color: '#2196f3' }}>Edit</Button>
                    </Box>
                    <Typography>{formData.spouse_first_name} {formData.spouse_middle_name} {formData.spouse_last_name}</Typography>
                    <Typography>DOB: {formData.spouse_dob}</Typography>
                    <Typography>{formData.spouse_email} | {formData.spouse_phone}</Typography>
                  </Box>
                )}

                {formData.children.some(c => c.first_name || c.last_name) && (
                  <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Children Information</Typography>
                      <Button size="small" variant="outlined" onClick={() => handleEditSection(3)}
                        sx={{ borderColor: '#2196f3', color: '#2196f3' }}>Edit</Button>
                    </Box>
                    {formData.children.filter(c => c.first_name || c.last_name).map((child, index) => (
                      <Typography key={index}>
                        {`${child.first_name} ${child.middle_name ? child.middle_name + ' ' : ''}${child.last_name}`.trim()} — {child.relationship} ({child.gender}, DOB: {child.dob}, Disabled: {child.disabled})
                      </Typography>
                    ))}
                  </Box>
                )}

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button variant="contained" onClick={handleBackFromSummary}
                    sx={{ minWidth: 120, backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' } }}>Back</Button>
                  <Button variant="contained" onClick={handleFinalSubmit} size="large" disabled={isSubmitting}
                    sx={{ minWidth: 220, fontSize: '1rem', backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold', padding: '12px 24px' }}>
                    {isSubmitting ? 'Saving...' : 'Register Member'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default AdminRegisterMember;